import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FingeringPart, FingeringMode, FingeringRestoreRequest } from '../../../types/fingering';
import { useGoogleDrivePicker } from '../../../hooks/useGoogleDrivePicker';
import { features } from '../../../config/features';
import { PART_LABELS } from '../../../constants/fingering';
import { parsePageRange } from '../../../utils/parsePageRange';
import { pageSetToRanges, rangesToPageSet } from '../../../utils/pageRangeConvert';
import { extractSelectedPages } from '../../../utils/extractSelectedPages';
import { formatDuration } from '../../../utils/formatDuration';
import {
  getFingeringMode,
  setFingeringMode,
  getFingeringPart,
  setFingeringPart,
} from '../../../utils/fingeringModeSettings';
import { useNow } from '../../../hooks/useNow';
import TuningSelector from './TuningSelector';
import PageRangeSelector from './PageRangeSelector';
import GoogleDriveIcon from '../../../components/icons/GoogleDriveIcon';

interface FingeringFormProps {
  onSubmit: (
    // issue #113 案C: 選択ページを抽出した PDF Blob を渡す (全選択/空は元 file)。
    // 抽出 Blob には name が無いため fileName を別引数で渡す。
    pdfBlob: Blob,
    fileName: string,
    part: FingeringPart,
    mode: FingeringMode,
    scordatura?: string,
    bpm?: number,
    // issue #143: 投入がブロックされた場合 (通知バリデーション等) は false を返す。
    // false のときフォームは #114 resetForm を走らせず入力を保持する。 それ以外 (void) は従来通りクリア。
  ) => boolean | void | Promise<boolean | void>;
  onFileChange: () => void;
  isProcessing: boolean;
  /** isProcessing=true のときマスコットの下に表示する文言。省略時は「処理中…お待ちください」。 */
  processingMessage?: string;
  /** issue #109: 処理中ジョブの起点 (createdAt, epoch ms)。指定時は spinner に経過を付与。
   *  未指定 = pre-jobID 窓 (起点未確定) → 経過は出さない。 */
  elapsedFromMs?: number;
  hasResult: boolean;
  /** issue #116 FE-2: 失敗ジョブ再投入でフォームへ復元する内容 (抽出済み PDF + 元設定)。未指定/null は無効。 */
  restoreRequest?: FingeringRestoreRequest | null;
  /** issue #116 FE-2: 復元の再適用トリガ。 同一 restoreRequest でも値が変われば復元 useEffect が再発火する。 */
  restoreNonce?: number;
}

const PARTS: { value: FingeringPart; label: string }[] = (Object.keys(PART_LABELS) as FingeringPart[]).map((value) => ({
  value,
  label: PART_LABELS[value],
}));

const MODES: { value: FingeringMode; label: string }[] = [
  { value: 'score', label: '総譜' },
  { value: 'part', label: 'パート譜' },
];

// issue #60: 選択ページの number[][] レンジを数字入力欄の圧縮文字列へ変換 (例 [[1,3],[5,5]] → '1-3,5')。
const rangesToLabel = (ranges: number[][]): string =>
  ranges.map(([s, e]) => (s === e ? `${s}` : `${s}-${e}`)).join(',');

// issue #109: spinner の経過表示を閉じた小コンポーネント。useNow をここに閉じ込め、
// FingeringForm 本体を毎秒 re-render させない (spec §6 性能)。
const SpinnerElapsed: React.FC<{ fromMs: number }> = ({ fromMs }) => {
  const now = useNow(true);
  return <>{formatDuration(now - fromMs)}</>;
};

const FingeringForm: React.FC<FingeringFormProps> = ({
  onSubmit,
  onFileChange,
  isProcessing,
  processingMessage = '処理中…お待ちください',
  elapsedFromMs,
  hasResult,
  restoreRequest,
  restoreNonce = 0,
}) => {
  const [file, setFile] = useState<File | null>(null);
  // issue #160: 楽譜モード / 総譜時のパートは localStorage 永続値から復元する (未設定・不正値は既定値)。
  const [part, setPart] = useState<FingeringPart>(getFingeringPart);
  const [mode, setMode] = useState<FingeringMode>(getFingeringMode);
  const [dropD, setDropD] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [bpm, setBpm] = useState<string>('');
  const [driveUrl, setDriveUrl] = useState('');
  const [settingsChanged, setSettingsChanged] = useState(false);
  // issue #93: (hasResult && !settingsChanged) ガードは同一入力・同一設定の二重生成防止が目的。
  // 新しいファイルの確定 = 入力が変わったので submit ガードを解除する (settingsChanged とは別フラグ)。
  const [fileChanged, setFileChanged] = useState(false);
  // issue #44 Phase γ-C/D: ページ範囲指定 input。
  // 空欄は「全ページ処理」 (= onSubmit に pageRange=undefined)、 不正入力はエラー表示で submit 抑止。
  const [pageRangeInput, setPageRangeInput] = useState<string>('');
  const [pageRangeError, setPageRangeError] = useState<string | null>(null);
  // issue #60: 選択ページ集合 (1-origin) を SSOT にする。サムネイル選択と数字入力の橋渡し。
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  // issue #69: 現行 PDF の総ページ数 (PageRangeSelector の onInit で確定、0=未確定)。
  // extractSelectedPages に渡して全ページ被覆時の無駄な PDFDocument.load を省くために使う。
  const [totalPages, setTotalPages] = useState(0);
  // issue #106: 詳細設定 (チューニング/ページ範囲/BPM) の折りたたみ開閉。既定は閉。
  const [detailOpen, setDetailOpen] = useState(false);
  // issue #116 FE-2: 失敗ジョブ再投入で復元した直後を示すフラグ (案C 制約の案内文言表示用)。
  // ユーザーが別ファイルを選ぶ / submit でリセットすると解除する。
  const [restoredNotice, setRestoredNotice] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // issue #60/#69: 別 PDF はページ構成が変わるため、ページ選択 (SSOT + 数字入力 + エラー) と
  // 既知総ページ数をまとめてリセットする。 全 caller が file 変更 (or file=null) を伴い、
  // 新 PDF の PageRangeSelector.onInit で totalPages は再設定される。
  const clearPageSelection = useCallback(() => {
    setSelectedPages(new Set());
    setPageRangeInput('');
    setPageRangeError(null);
    setTotalPages(0);
  }, []);

  const handleDriveFile = useCallback((driveFile: File) => {
    setFile(driveFile);
    clearPageSelection();
    setFileChanged(true);
    // issue #116 FE-2: ユーザーが新しい PDF を選んだので復元由来の案内文言は不要。
    setRestoredNotice(false);
    onFileChange();
  }, [onFileChange, clearPageSelection]);

  const {
    state: driveState,
    signInAndPick,
    openPicker,
    downloadFromUrl,
    isConfigured: isDriveConfigured,
    isReady: isDriveReady,
  } = useGoogleDrivePicker(handleDriveFile);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile?.type === 'application/pdf') {
      setFile(droppedFile);
      clearPageSelection();
      setFileChanged(true);
      setRestoredNotice(false);
      onFileChange();
    }
  }, [onFileChange, clearPageSelection]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      clearPageSelection();
      setFileChanged(true);
      setRestoredNotice(false);
      onFileChange();
    }
    // issue #147: 同一ファイルの再選択でも change を発火させるため input value をクリアする。
    // submit 後 resetForm は React state (file) のみ null 化し DOM input.value を残すため、
    // 同一ファイル再選択がブラウザの dedup で no-op になり preview が出ない不具合を防ぐ。
    e.target.value = '';
  }, [onFileChange, clearPageSelection]);

  // issue #160: mode/part は永続設定。 state と localStorage は必ず対で更新する
  // (次回訪問時・投入後 resetForm でユーザーの選択が失われないようにする)。
  const handleModeChange = useCallback((next: FingeringMode) => {
    setMode(next);
    setFingeringMode(next);
    setSettingsChanged(true);
  }, []);

  const handlePartChange = useCallback((next: FingeringPart) => {
    setPart(next);
    setFingeringPart(next);
    setSettingsChanged(true);
  }, []);

  // issue #114: 投入後にフォームを初期値へ戻す (#99 出力クリアと対称の入力クリア)。
  // FingeringForm 内 state のみ対象。localStorage / 永続トグル (自動保存/メール) には触れない。
  // dragOver は drag 中のみ true の一時状態で submit 時は既に false のため対象外。
  const resetForm = useCallback(() => {
    setFile(null);
    // issue #160: mode/part は永続設定なので「既定値」ではなく「永続値」へ戻す
    // (投入のたびにユーザーの選んだモード/パートが失われないようにする)。
    setPart(getFingeringPart());
    setMode(getFingeringMode());
    setDropD(false);
    setBpm('');
    setDriveUrl('');
    clearPageSelection();
    setDetailOpen(false);
    setSettingsChanged(false);
    setFileChanged(false);
    // issue #116 FE-2: 投入後クリアで復元由来の案内文言も消す (#114 と対称)。
    setRestoredNotice(false);
  }, [clearPageSelection]);

  // issue #116 FE-2: 失敗ジョブ再投入でフォームへ復元する (restoreRequest / restoreNonce 変化時)。
  // 抽出済み PDF を File 化してフォームへ載せ、part/mode/dropD(scordatura由来)/bpm を復元する。
  // 案C 制約: PDF は選択ページのみ抽出済みで原本は残らないため、 selectedPages は空 (= 全ページ)
  // に戻し、 案内文言 (restoredNotice) で「別ページはトップから再アップロード」を促す。
  // 発火は restoreNonce に依存 (親が復元のたびに bump)。 restore は submit 前の投入なので
  // #114 resetForm (submit 時クリア) とは衝突しない。
  useEffect(() => {
    if (!restoreRequest) return;
    // 抽出 Blob には name が無いため fileName を持つ File に包む (サムネ / 出力名に使う)。
    const restoredFile = new File([restoreRequest.file], restoreRequest.fileName, {
      type: 'application/pdf',
    });
    setFile(restoredFile);
    setPart(restoreRequest.part);
    setMode(restoreRequest.mode);
    setDropD(restoreRequest.scordatura === 'VI=D');
    setBpm(restoreRequest.bpm !== undefined ? String(restoreRequest.bpm) : '');
    clearPageSelection();
    // 復元 = 入力が変わったので submit ガードを解除する (新ファイル確定と同じ扱い)。
    setFileChanged(true);
    setSettingsChanged(true);
    setRestoredNotice(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreNonce]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const scordatura = dropD ? 'VI=D' : undefined;
    const bpmValue = bpm !== '' ? Number(bpm) : undefined;
    // issue #113 案C: 選択ページ (SSOT=selectedPages) を frontend で抽出し、抽出済み PDF を送る。
    // 空選択/全ページ被覆は元 file をそのまま返す。抽出失敗 (上限超等) はエラー表示で投入を止める。
    try {
      // issue #69: totalPages (既知の総ページ数) を渡し、全ページ被覆時の無駄な load を省く。
      const blob = await extractSelectedPages(file, selectedPages, totalPages);
      const result = await onSubmit(blob, file.name, part, mode, scordatura, bpmValue);
      // issue #114: 投入内容を onSubmit に渡した直後にフォームを初期化する。
      // issue #143: onSubmit が false を返した = 投入ブロック (通知バリデーション等) なので
      // リセットせず入力を保持し、 ユーザーがその場で直して再投入できるようにする。
      if (result !== false) resetForm();
    } catch (err) {
      setPageRangeError(err instanceof Error ? err.message : 'ページ抽出に失敗しました');
    }
  }, [file, part, mode, dropD, bpm, selectedPages, totalPages, onSubmit, resetForm]);

  const handleDriveUrlSubmit = useCallback(() => {
    if (!driveUrl.trim()) return;
    downloadFromUrl(driveUrl.trim());
    setDriveUrl('');
  }, [driveUrl, downloadFromUrl]);

  const showPart = mode === 'score';
  const isFormDisabled = isProcessing;
  const isSubmitDisabled =
    !file || isProcessing || (hasResult && !settingsChanged && !fileChanged) || pageRangeError !== null;

  // issue #44 Phase γ-D / #60: 数字入力 onChange — 即時に parsePageRange でバリデーションし、
  // parse 成功時は SSOT (selectedPages) へ一方向反映する (数字 → Set)。
  const handlePageRangeChange = useCallback((value: string) => {
    setPageRangeInput(value);
    setSettingsChanged(true);
    if (value.trim() === '') {
      setPageRangeError(null);
      setSelectedPages(new Set());
      return;
    }
    const parsed = parsePageRange(value);
    if (parsed === null) {
      setPageRangeError('ページ範囲が正しくありません (例: 1-5, 7-9)');
    } else {
      setPageRangeError(null);
      setSelectedPages(rangesToPageSet(parsed));
    }
  }, []);

  // issue #60: サムネイル選択 onChange — SSOT を更新し、数字入力欄へ圧縮文字列を同期表示する。
  const handleSelectorChange = useCallback((next: Set<number>) => {
    setSelectedPages(next);
    setSettingsChanged(true);
    setPageRangeInput(rangesToLabel(pageSetToRanges(next)));
    setPageRangeError(null);
  }, []);

  // issue #31: サムネ描画完了時の全ページ初期選択。ユーザー操作ではないので settingsChanged を立てず、
  // 数字入力欄も空のまま (「空欄=全ページ」の既定表示を保つ)。
  // issue #69: 全ページ集合 = 総ページ数なので、 このタイミングで totalPages を確定する
  // (extractSelectedPages の pre-I/O 判定に使う)。
  const handleSelectorInit = useCallback((next: Set<number>) => {
    setSelectedPages(next);
    setTotalPages(next.size);
  }, []);

  // issue #106: 詳細設定が閉じていても、デフォルトから変更された設定をバッジで提示する
  // (「隠す」のではなく「畳む + 中身を見せる」で機能の存在に気づけるようにする)。
  const detailBadges: string[] = [];
  if (dropD) detailBadges.push('Drop D');
  if (pageRangeInput.trim() !== '' && pageRangeError === null) {
    detailBadges.push(`ページ ${pageRangeInput.trim()}`);
  }
  if (features.enableAudio && bpm.trim() !== '') detailBadges.push(`${bpm.trim()} BPM`);

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* PDF Upload */}
      <div
        className={`relative group border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
          isFormDisabled
            ? 'opacity-50 cursor-not-allowed'
            : dragOver
              ? 'border-amber-400 bg-amber-50 scale-[1.02] cursor-pointer'
              : file
                ? 'border-green-400 bg-green-50/50 cursor-pointer'
                : 'border-gray-300 hover:border-amber-300 hover:bg-amber-50/30 cursor-pointer'
        }`}
        onDragOver={(e) => { e.preventDefault(); if (!isFormDisabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { if (!isFormDisabled) handleDrop(e); else e.preventDefault(); }}
        onClick={() => { if (!isFormDisabled) fileInputRef.current?.click(); }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
          className="hidden"
          data-testid="pdf-file-input"
        />
        {file ? (
          <div className="space-y-1">
            <svg className="mx-auto w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-700 font-semibold text-sm">{file.name}</p>
            <p className="text-xs text-gray-400">クリックで変更</p>
          </div>
        ) : (
          <div className="space-y-2">
            <svg className="mx-auto w-10 h-10 text-gray-300 group-hover:text-amber-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <p className="text-gray-500 text-sm">PDF楽譜をドラッグ&ドロップ</p>
            <p className="text-xs text-gray-400">またはクリックして選択</p>
          </div>
        )}
      </div>

      {/* issue #116 FE-2: 失敗ジョブ再投入で復元した直後の案内 (案C 制約)。
          復元 PDF は前回の抽出済み (選択ページのみ) なので、 別ページを含めたい場合は
          トップから再アップロードが必要なことを軽く案内する。 */}
      {restoredNotice && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
          <span aria-hidden="true">ℹ️</span>
          <span>
            前回の抽出済みPDFで再投入します。設定を変えて「運指を生成」してください。
            別のページを選ぶ場合はトップから再アップロードしてください。
          </span>
        </div>
      )}

      {/* Google Drive Integration */}
      {/* issue #104: 入力ソース間の「── または ──」divider を廃止し、Google Drive を
          D&D 直下のセカンダリ手段として隣接配置する (区切り線なし、主(D&D)＋補助(Drive)の階層)。 */}
      {isDriveConfigured && (
        <div className="space-y-3">
          {/* Picker button (issue #35 UX 改善: ラベル固定 + 認証→picker 貫通)。
              ログイン済 (isDriveReady=true): cache 済 token で picker を開く (openPicker)。
              未ログイン: consent → 成功後に picker を貫通表示 (signInAndPick)。 */}
          <button
            type="button"
            onClick={isDriveReady ? openPicker : signInAndPick}
            disabled={isFormDisabled || driveState.isLoading}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold transition-all ${
              isFormDisabled || driveState.isLoading
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 hover:text-blue-700 active:scale-[0.98]'
            }`}
          >
            {driveState.isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Drive から取得中...
              </>
            ) : (
              <>
                <GoogleDriveIcon className="w-5 h-5" />
                {/* issue #35 UX 改善: ラベルは状態に依らず常に「Google Drive から選択」固定。 */}
                Google Drive から選択
              </>
            )}
          </button>

          {/* Drive URL input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={driveUrl}
              onChange={(e) => setDriveUrl(e.target.value)}
              placeholder="Drive のリンクを貼り付け"
              disabled={isFormDisabled || driveState.isLoading}
              className={`flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm transition-all outline-none ${
                isFormDisabled || driveState.isLoading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-50 focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100'
              }`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleDriveUrlSubmit();
                }
              }}
            />
            <button
              type="button"
              onClick={handleDriveUrlSubmit}
              disabled={isFormDisabled || driveState.isLoading || !driveUrl.trim()}
              className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                isFormDisabled || driveState.isLoading || !driveUrl.trim()
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600 active:scale-[0.98]'
              }`}
            >
              取得
            </button>
          </div>

          {/* Drive error */}
          {driveState.error && (
            <p className="text-xs text-red-500 flex items-center gap-1">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01" />
              </svg>
              {driveState.error}
            </p>
          )}
        </div>
      )}

      {/* 常時表示: 楽譜モード / パート。アップロードした PDF の種別は結果を左右する入力なので畳まない。 */}
      <div className="space-y-3">
        <div>
          <label htmlFor="mode-select" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">楽譜モード</label>
          <select
            id="mode-select"
            value={mode}
            onChange={(e) => handleModeChange(e.target.value as FingeringMode)}
            disabled={isFormDisabled}
            className={`w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm transition-all outline-none ${
              isFormDisabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100'
            }`}
          >
            {MODES.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {showPart && (
          <div>
            <label htmlFor="part-select" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">パート</label>
            <select
              id="part-select"
              value={part}
              onChange={(e) => handlePartChange(e.target.value as FingeringPart)}
              disabled={isFormDisabled}
              className={`w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm transition-all outline-none ${
                isFormDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100'
              }`}
            >
              {PARTS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* issue #60: file 確定後に PDF サムネイル選択パネルを展開 (選択の「主」、数字入力は詳細設定内のフォールバック)。
          PageRangeSelector は pageCount 取得まで null を返すため、 file 未確定時や描画前は何も表示しない。 */}
      {file && (
        <PageRangeSelector
          pdfBlob={file}
          selectedPages={selectedPages}
          onChange={handleSelectorChange}
          onInit={handleSelectorInit}
        />
      )}

      {/* issue #106: 詳細設定 (チューニング / 処理対象ページ範囲 / テンポBPM) を折りたたみへ畳む。
          常時表示を必須入力に絞りつつ、閉じていてもデフォルトから変更された値をバッジで示すことで
          「隠す」ではなく「畳む + 中身を見せる」を実現し、機能の存在に気づけるようにする。
          閉時は inert で中身をフォーカス/支援技術から外す。 */}
      <div>
        <button
          type="button"
          onClick={() => setDetailOpen((open) => !open)}
          disabled={isFormDisabled}
          aria-expanded={detailOpen}
          aria-controls="fingering-detail-settings"
          className={`w-full flex items-center gap-2 py-1.5 px-1 rounded-lg text-left transition-colors ${
            isFormDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-amber-50/60 cursor-pointer'
          }`}
        >
          <svg
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${detailOpen ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">詳細設定</span>
          <span className="text-xs text-gray-300 font-normal normal-case">任意</span>
          {!detailOpen && detailBadges.length > 0 && (
            <span className="ml-auto flex items-center gap-1 flex-wrap justify-end">
              {detailBadges.map((badge) => (
                <span
                  key={badge}
                  className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-semibold whitespace-nowrap"
                >
                  {badge}
                </span>
              ))}
            </span>
          )}
        </button>

        <div
          id="fingering-detail-settings"
          role="region"
          aria-label="詳細設定"
          aria-hidden={!detailOpen}
          inert={!detailOpen}
          className={`overflow-hidden transition-all duration-300 ${
            detailOpen ? 'max-h-[640px] opacity-100 mt-1' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="space-y-3 border-l-2 border-amber-200/60 ml-1.5 pl-3 pr-1 pt-2 pb-1">
            {/* Tuning Toggle */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                チューニング <span className="text-gray-300 font-normal normal-case">任意</span>
              </label>
              <TuningSelector
                value={dropD ? 'drop_d' : 'standard'}
                disabled={isFormDisabled}
                options={[
                  { value: 'standard', label: '標準' },
                  { value: 'drop_d', label: 'Drop D' },
                ]}
                onChange={(next) => {
                  const nextDropD = next === 'drop_d';
                  if (nextDropD !== dropD) {
                    setDropD(nextDropD);
                    setSettingsChanged(true);
                  }
                }}
              />
            </div>

            {/* issue #44 Phase γ-C/D: ページ範囲指定 input (空欄で全ページ処理) */}
            <div>
              <label
                htmlFor="page-range-input"
                className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
              >
                処理対象ページ範囲 <span className="text-gray-300 font-normal normal-case">空欄で全ページ</span>
              </label>
              <input
                id="page-range-input"
                type="text"
                value={pageRangeInput}
                placeholder="1-5,7-9"
                onChange={(e) => handlePageRangeChange(e.target.value)}
                disabled={isFormDisabled}
                aria-invalid={pageRangeError !== null}
                aria-describedby={pageRangeError ? 'page-range-error' : undefined}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm transition-all outline-none ${
                  isFormDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                    : pageRangeError
                      ? 'border-red-300 bg-red-50 focus:bg-white focus:border-red-500 focus:ring-2 focus:ring-red-100'
                      : 'border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100'
                }`}
              />
              {pageRangeError && (
                <p id="page-range-error" className="mt-1 text-xs text-red-600">
                  {pageRangeError}
                </p>
              )}
            </div>

            {features.enableAudio && (
              <div>
                <label htmlFor="bpm-input" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  テンポ (BPM) <span className="text-gray-300 font-normal normal-case">空欄で自動検出</span>
                </label>
                <input
                  id="bpm-input"
                  type="number"
                  min={30}
                  max={300}
                  value={bpm}
                  placeholder="120（自動）"
                  onChange={(e) => { setBpm(e.target.value); setSettingsChanged(true); }}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all outline-none"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit */}
      {isProcessing ? (
        <div className="w-full py-6 flex flex-col items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-brand-purple/20 animate-pulse-ring" />
            <img
              src="/omakase-unshi-kun.png"
              alt=""
              className="w-12 h-12 relative z-10 animate-bounce"
              style={{ animationDuration: '1.5s' }}
            />
          </div>
          <p className="text-sm font-bold text-gray-500 animate-pulse">
            {processingMessage}
            {elapsedFromMs !== undefined && <> <SpinnerElapsed fromMs={elapsedFromMs} /></>}
          </p>
        </div>
      ) : (
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className={`w-full py-3.5 rounded-xl font-bold text-white transition-all text-base ${
            isSubmitDisabled
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-brand-purple to-indigo-500 hover:from-purple-700 hover:to-indigo-600 shadow-lg shadow-purple-200 hover:shadow-purple-300 active:scale-[0.98]'
          }`}
        >
          {/* issue #112: 「設定を変更して再生成」への切り替えを廃止し、常に「運指を生成」。 */}
          運指を生成
        </button>
      )}
    </form>
  );
};

export default FingeringForm;
